import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLinkToken, unlinkPlatform, getPlatforms } from '../services/api';

function DiscordLogo({ size = 20, color = '#5865F2' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

function SteamLogo({ size = 20, color = '#66c0f4' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.497 1.009 2.455-.397.957-1.494 1.411-2.455 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z"/>
    </svg>
  );
}

function TwitchLogo({ size = 20, color = '#9146FF' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  );
}

const PLATFORM_META = {
  discord: {
    label: 'Discord',
    color: '#5865F2',
    Icon: DiscordLogo,
  },
  steam: {
    label: 'Steam',
    color: '#1b2838',
    iconColor: '#66c0f4',
    Icon: SteamLogo,
  },
  twitch: {
    label: 'Twitch',
    color: '#9146FF',
    Icon: TwitchLogo,
  },
};

export default function LinkedPlatforms() {
  const { user, refreshUser } = useAuth();
  const [configured, setConfigured] = useState({ discord: false, steam: false, twitch: false });
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    getPlatforms()
      .then(setConfigured)
      .catch(() => {});
  }, []);

  const handleConnect = async (platform) => {
    setError('');
    setLoading((p) => ({ ...p, [platform]: true }));
    try {
      const { token } = await getLinkToken();
      sessionStorage.setItem('nr_returnTo', window.location.pathname);
      const serverUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      window.location.href = `${serverUrl}/api/auth/${platform}?linkToken=${token}`;
    } catch (err) {
      setError(err.response?.data?.error || `Failed to initiate ${platform} connection.`);
      setLoading((p) => ({ ...p, [platform]: false }));
    }
  };

  const handleDisconnect = async (platform) => {
    setError('');
    setLoading((p) => ({ ...p, [platform]: true }));
    try {
      await unlinkPlatform(platform);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to disconnect ${platform}.`);
    } finally {
      setLoading((p) => ({ ...p, [platform]: false }));
    }
  };

  const linked = user?.linkedPlatforms || {};

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>
        LINKED PLATFORMS
      </div>

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.entries(PLATFORM_META).map(([platform, meta]) => {
          const isLinked = !!linked[platform];
          const isConfigured = configured[platform];
          const isLoading = !!loading[platform];
          const info = linked[platform];

          return (
            <div
              key={platform}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 8,
                border: `1px solid ${isLinked ? meta.color + '55' : 'var(--border)'}`,
                background: isLinked ? meta.color + '11' : 'var(--bg-secondary)',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <meta.Icon size={20} color={isLinked ? (meta.iconColor || meta.color) : 'var(--text-muted)'} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: isLinked ? meta.color : 'var(--text-secondary)' }}>
                    {meta.label}
                  </div>
                  {isLinked && info && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {info.username || info.displayName || info.id}
                    </div>
                  )}
                  {!isLinked && !isConfigured && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>Not configured on server</div>
                  )}
                </div>
              </div>

              {isLinked ? (
                <button
                  onClick={() => handleDisconnect(platform)}
                  disabled={isLoading}
                  style={{
                    background: 'none', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '4px 12px',
                    color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12,
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {isLoading ? '...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(platform)}
                  disabled={!isConfigured || isLoading}
                  title={!isConfigured ? `${meta.label} is not configured on the server` : undefined}
                  style={{
                    background: isConfigured ? meta.color : 'var(--border)',
                    border: 'none', borderRadius: 6, padding: '4px 12px',
                    color: '#fff', cursor: isConfigured ? 'pointer' : 'not-allowed',
                    fontSize: 12, fontWeight: 600,
                    whiteSpace: 'nowrap', flexShrink: 0,
                    opacity: isConfigured ? 1 : 0.4,
                  }}
                >
                  {isLoading ? '...' : 'Connect'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
